/* eslint-disable max-len */
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@chakra-ui/react';
import { useMemo } from 'react';
import { EntryMethodPreference, EntryMethodPreferenceOption, EntryMethodPreferenceSettings } from '@acp/shared';
import { format, isValid } from 'date-fns';
import { entryMethodFormSchema, EntryMethodFormValues, ReferenceValue } from '../components/entry-method-modal/form-schema';
import { useEntryMethodMutation } from '../components/entry-method-modal/use-update-entry-method-mutation';
import { usePropertyEntryMethodPreference } from './use-entry-method';
import { useAgencyEntryMethodPreference } from '../../agency';

export type PreferenceData = {
    agencyPreference: { [key in EntryMethodPreference]: EntryMethodPreferenceOption | null } | null;
    propertyPreference: EntryMethodPreferenceSettings | null;
};

export function useEntryMethodForm({
    propertyId,
    onEntryMethodUpdated,
}: {
    propertyId: number;
    onEntryMethodUpdated?: () => void;
}) {
    const toast = useToast();

    const {
        data: propertyPreference,
        isLoading: isPropertyPreferenceLoading,
    } = usePropertyEntryMethodPreference({ propertyId });

    const {
        data: agencyPreference,
        isLoading: isAgencyPreferenceLoading,
    } = useAgencyEntryMethodPreference();

    const isLoading = useMemo(
        () => isPropertyPreferenceLoading || isAgencyPreferenceLoading,
        [isPropertyPreferenceLoading, isAgencyPreferenceLoading]
    );

    // get agency and property preferences
    const entryMethodPreferenceData = useMemo(() => {
        if (isLoading || propertyPreference === undefined || agencyPreference === undefined) return null;

        return {
            propertyPreference,
            agencyPreference,
        } as PreferenceData;
    }, [isLoading, agencyPreference, propertyPreference]);

    // get expiry date message which show below the date picker
    const expDateMessage = useMemo(() => {
        if (entryMethodPreferenceData?.propertyPreference?.tempExpireDateSettings) {
            const { min, max } = entryMethodPreferenceData.propertyPreference.tempExpireDateSettings;
            return `Available from ${format(min, 'dd/MM/yyyy')} to ${format(max, 'dd/MM/yyyy')}`;
        }

        return null;
    }, [entryMethodPreferenceData]);

    // get default value for the field option (radio button)
    const setFieldOptionValue = (
        preferences: Record<string, EntryMethodPreferenceOption | null>,
        tempPreferences: Record<string, EntryMethodPreferenceOption | null>,
        preferenceKey: string
    ): ReferenceValue | undefined => {
        if (preferences[preferenceKey] === EntryMethodPreferenceOption.YES) {
            return ReferenceValue.ALWAYS_ALLOWED;
        } if (tempPreferences[preferenceKey] === EntryMethodPreferenceOption.YES
            || tempPreferences[preferenceKey] !== null) {
            return ReferenceValue.ALLOW_UNTIL_DATE;
        }
        return undefined;
    };

    // set default value of fieldOption for the form
    const setDefaultFieldOptionValues = (
        preferences: Record<string, EntryMethodPreferenceOption | null>,
        tempPreferences: Record<string, EntryMethodPreferenceOption | null>,
        defaultValues: EntryMethodFormValues
    ) => {
        const updatedFieldOption = { ...defaultValues.fieldOption };

        for (const key of Object.keys(updatedFieldOption)) {
            updatedFieldOption[key as keyof typeof updatedFieldOption] = setFieldOptionValue(
                preferences,
                tempPreferences,
                key
            );
        }
        return updatedFieldOption;
    };

    // get default values for the form
    const defaultFormValues = useMemo(() => {
        let defaultValues: EntryMethodFormValues = {
            fieldOption: {
                occupantAppointmentPreference: undefined,
                internalEnPreference: undefined,
                vacantEntryPreference: undefined,
            },
            comments: undefined,
            preferences: {},
            tempPreferences: {},
            tempExpireDateSettings: propertyPreference?.tempExpireDateSettings,
        };

        if (entryMethodPreferenceData !== null) {
            // incase this property has property preference
            if (entryMethodPreferenceData.propertyPreference?.preferences) {
                const { preferences, tempPreferences } = entryMethodPreferenceData.propertyPreference;

                defaultValues.preferences = { ...preferences };
                defaultValues.tempPreferences = { ...tempPreferences };

                // set default value of fieldOption depends on preferences and tempPreferences
                const updatedFieldOption = setDefaultFieldOptionValues(preferences, tempPreferences || {}, defaultValues);
                defaultValues.fieldOption = { ...updatedFieldOption };
            } else if (entryMethodPreferenceData.agencyPreference !== null) {
                // if we don't have property preference, use agency preference instead
                defaultValues.preferences = { ...entryMethodPreferenceData.agencyPreference };

                const updatedFieldOption = setDefaultFieldOptionValues(entryMethodPreferenceData.agencyPreference, {}, defaultValues);
                defaultValues.fieldOption = { ...updatedFieldOption };
            }
        }

        return defaultValues;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entryMethodPreferenceData]);

    const entryMethodForm = useForm<EntryMethodFormValues>({
        resolver: zodResolver(entryMethodFormSchema),
        defaultValues: defaultFormValues,
        values: defaultFormValues,
        mode: 'onChange',
    });
    const { reset, setValue } = entryMethodForm;

    const entryMethodMutation = useEntryMethodMutation(propertyId, {
        onSuccess: () => {
            toast({
                title: 'Entry method preference',
                description: 'Your preferences have been updated successfully',
                status: 'success',
            });

            onEntryMethodUpdated?.();
            reset();
        },
    });

    // handle select radio button
    // set field preferences and tempPreferences value based on the selected option
    const handleSelectOption = (
        { fieldName, optionValue }:
        { fieldName: keyof EntryMethodFormValues['fieldOption']; optionValue: ReferenceValue }
    ) => {
        setValue(`fieldOption.${fieldName}`, optionValue);

        if (optionValue === ReferenceValue.ALLOW_UNTIL_DATE) {
            setValue(`preferences.${fieldName}`, EntryMethodPreferenceOption.NO);
            setValue(`tempPreferences.${fieldName}`, EntryMethodPreferenceOption.YES);
        } else {
            setValue(`preferences.${fieldName}`, EntryMethodPreferenceOption.YES);
            setValue(`tempPreferences.${fieldName}`, null);
        }
    };

    const handleConfirm = (formValues: EntryMethodFormValues) => {
        // format data
        const formattedData = {
            preferences: { ...formValues.preferences },
            tempPreferences: { ...formValues.tempPreferences },
        };

        // run though all the keys in tempPreferences (not keys with ExpiresAt)
        for (const key of Object.keys(formattedData.tempPreferences)) {
            if (!key.endsWith('ExpiresAt')) {
                const expiresAtKey = `${key}ExpiresAt`;

                // if any key has value, then its ExpiresAt key will be formatted
                if (formattedData.tempPreferences[key] !== null && formattedData.tempPreferences[key] !== undefined) {
                    const dateValue = formattedData.tempPreferences[expiresAtKey] as Date;

                    formattedData.tempPreferences[expiresAtKey] = isValid(new Date(dateValue))
                        ? format(new Date(dateValue), 'yyyy-MM-dd')
                        : null;
                }

                // If any key in tempPreferences is null, set its corresponding ExpiresAt to null
                if (formattedData.tempPreferences[key] === null) {
                    formattedData.tempPreferences[expiresAtKey] = null;
                }
            }
        }

        // then submit
        entryMethodMutation.mutate(formattedData);
    };

    return {
        entryMethodForm,
        entryMethodMutation,
        entryMethodPreferenceData,
        expDateMessage,
        isLoading,
        handleConfirm,
        handleSelectOption,
    };
}
